import { io } from "socket.io-client";
import { ref } from "vue";
const formatValue = (obj) => {
    if (typeof obj === "string") {
        return obj.length > 50 ? `${obj.slice(0, 50)}...` : obj;
    }
    if (Array.isArray(obj)) {
        return obj.map(formatValue);
    }
    if (obj &&
        typeof obj === "object" &&
        (typeof Buffer === "undefined" || !Buffer.isBuffer(obj))) {
        return Object.entries(obj).reduce((result, [key, value]) => ({
            ...result,
            [key]: formatValue(value),
        }), {});
    }
    return obj;
};
export const stringifyEventParameters = (args) => {
    let stringified = args
        .map((arg) => JSON.stringify(formatValue(arg)))
        .join(", ");
    if (stringified.length > 100) {
        stringified = stringified.substring(0, 100) + "...";
    }
    return stringified;
};
export class SocketClient {
    constructor(socketRootUrl) {
        this.socketRootUrl = socketRootUrl;
        this.cacheHydrator = {
            state: ref(),
            run: async (loadCachedDataFn, loadRealDataFn) => {
                this.cacheHydrator.state = ref({
                    mode: "LOAD_CACHE",
                    cachedCallsDone: [],
                    hydratedCallsDoneAmount: 0,
                });
                console.debug("loading cache...");
                await loadCachedDataFn();
                this.cacheHydrator.state.value.mode = "HYDRATE";
                this.cacheHydrator.state.value.hydratedCallsDoneAmount = 0;
                console.debug("Hydrating...");
                loadRealDataFn();
            },
        };
        this.onConnectError = (e, namespace, _eventName) => {
            console.error(`${namespace}: connect_error: ${e}`);
        };
        this.onConnected = (namespace) => {
            console.info(`${namespace}: connected`);
        };
    }
    addNamespace(namespaceName, namespaceOptions = {}) {
        const { session, cache } = namespaceOptions;
        let socket;
        let isOffline;
        const ongoingCalls = ref([]);
        const connect = () => {
            console.log("connect");
            console.log(`connecting to ${namespaceName} at ${new Date().toISOString()}`);
            socket = io(this.socketRootUrl + namespaceName, {
                extraHeaders: {
                    "X-Namespace": namespaceName,
                },
                timeout: 1000,
                transports: ["websocket"],
                multiplex: false,
                auth: async (cb) => {
                    const token = await session?.getToken();
                    cb(token ? { token } : {});
                },
            })
                .onAny((event, ...args) => {
                if (!["connect", "connect_error"].includes(event)) {
                    console.debug(`${namespaceName}/${event} received`, args);
                }
            })
                .on("connect_error", (e) => {
                isOffline = true;
                console.log("connect_error", namespaceName, e);
                this.onConnectError(e, namespaceName);
            })
                .on("connect", () => {
                isOffline = false;
                console.log(`connected to ${namespaceName} at ${new Date().toISOString()}`);
                this.onConnected(namespaceName);
            });
        };
        return new Proxy({}, {
            set: (_, event, callback) => {
                socket?.on(event, callback);
                return true;
            },
            get: (_, eventName) => {
                switch (eventName) {
                    case "_socket":
                        return socket;
                    case "_connect":
                        return connect;
                    case "_ongoingCalls":
                        return ongoingCalls;
                    case "__proto__":
                    case "toJSON":
                        return null;
                }
                return async (...args) => {
                    if (!socket) {
                        connect();
                    }
                    const startTime = Date.now();
                    const lastArg = [...args].pop();
                    const isCacheDisabledByNamespaceOptions = namespaceOptions.cache?.disableCache?.(eventName);
                    const isCacheDisabledByLastArg = lastArg &&
                        typeof lastArg === "object" &&
                        "disableCache" in lastArg &&
                        lastArg.disableCache;
                    if (isCacheDisabledByLastArg) {
                        args.pop();
                    }
                    const disableCache = isCacheDisabledByNamespaceOptions || isCacheDisabledByLastArg;
                    const shortEventConsoleString = `${eventName}(${stringifyEventParameters(args)})`;
                    const eventConsoleString = `${namespaceName}/${shortEventConsoleString}`;
                    const debugCall = async (post = false, cached = false) => {
                        const token = await session?.getToken();
                        if (eventName !== "toJSON") {
                            if (cached) {
                                console.debug(`${eventConsoleString} served from cache`);
                            }
                            else {
                                console.debug(`${eventConsoleString} ${post
                                    ? `responded in ${Date.now() - startTime}ms`
                                    : `called ${token ? "with token" : "without token"}`} at ${new Date().toISOString()}`);
                                if (post) {
                                    ongoingCalls.value = ongoingCalls.value.filter((call) => call !== shortEventConsoleString);
                                }
                                else {
                                    ongoingCalls.value = ongoingCalls.value.concat(shortEventConsoleString);
                                }
                            }
                        }
                    };
                    let isCacheUsed = false;
                    let cacheKey;
                    if (cache && !disableCache) {
                        cacheKey = `${namespaceName}/${eventName} ${JSON.stringify(args)}`;
                        const cacheData = await cache.storage.get(cacheKey, {
                            cache: {
                                ttl: isOffline ||
                                    this.cacheHydrator.state.value?.mode === "LOAD_CACHE"
                                    ? undefined
                                    : typeof cache.ttl === "function"
                                        ? cache.ttl(eventName, args)
                                        : cache.ttl,
                            },
                        });
                        isCacheUsed =
                            cacheData !== undefined &&
                                !(typeof cacheData === "object" && cacheData.state === "empty");
                        if (isCacheUsed) {
                            debugCall(true, true);
                            if (this.cacheHydrator.state.value) {
                                switch (this.cacheHydrator.state.value.mode) {
                                    case "LOAD_CACHE":
                                        this.cacheHydrator.state.value.cachedCallsDone.push(eventConsoleString);
                                        break;
                                    case "HYDRATE":
                                        if (this.cacheHydrator.state.value.cachedCallsDone.includes(eventConsoleString)) {
                                            this.cacheHydrator.state.value.hydratedCallsDoneAmount++;
                                        }
                                        break;
                                }
                            }
                            return cacheData;
                        }
                    }
                    socket.on("connect_error", (e) => {
                        isOffline = true;
                        this.onConnectError(e.message === "websocket error"
                            ? {
                                message: "offline_no_cache",
                                name: "offline_no_cache",
                            }
                            : e, namespaceName, eventName);
                    });
                    await debugCall();
                    const data = await socket.emitWithAck(eventName, ...args);
                    if (data && typeof data === "object" && "error" in data) {
                        throw data;
                    }
                    await debugCall(true);
                    if (cache && cacheKey) {
                        cache.storage.set(cacheKey, data, {
                            timeout: typeof cache.ttl === "function"
                                ? cache.ttl(eventName, args)
                                : cache.ttl,
                        });
                    }
                    if (this.cacheHydrator.state.value?.mode === "HYDRATE" &&
                        this.cacheHydrator.state.value.cachedCallsDone.includes(eventConsoleString)) {
                        this.cacheHydrator.state.value.hydratedCallsDoneAmount++;
                    }
                    return data;
                };
            },
        });
    }
}
export { buildStorage, buildWebStorage } from "axios-cache-interceptor";
